import PageBannerSlot from '@/components/common/banner/PageBannerSlot';
import { usePageBanners } from '@/components/common/banner/usePageBanners';
import { ListPageShell, ListTabRow, ListTabs, ListTypeTab, ListFilterRow, ListBody, ListCreateButton, useListHeaderHeight } from '@/components/common/organism/ListPageLayout';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import Pagination from '@/components/common/elements/Pagination';
import LoadingSpinner from '@/components/common/elements/LoadingSpinner';
import PageState from '@/components/common/PageState';
import styled from '@emotion/styled';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import Text from '@/components/common/elements/Text';
import { SeminarSummary } from './types';
import { api } from '@/api/repository/axios';
import { useListRestore, useScrollRestore } from '@/common/listRestore';
import { FILTER_TOUCHED_STATE, useSavedFilterOnEntry } from '@/common/savedListFilter';
import { userAtom } from '@/state/loginState';
import SeminarFilterBar from '@/pages/Seminar/components/SeminarFilterBar';
import type { CategoryGroupOption } from '@/pages/Seminar/components/SeminarFilterBar';
import SeminarCard from '@/components/domain/seminar/SeminarCard';
import { CAREER_COLOR, isCareerSeminar, sortCareerFirst } from '@/components/domain/seminar/seminarDisplay';
import { CheckToggle, ListControlRow, SortSelect, filterTokens, listControlGap, useIsMobileWidth } from '@/components/common/filter';
import {
  SEMINAR_SORT_OPTIONS,
  SEMINAR_TYPE_TABS,
  clearSeminarConditions,
  hasSeminarCondition,
  normalizeFormatsForTab,
  isPlainSeminarEntry,
  parseSavedSeminarQuery,
  parseSeminarListQuery,
  savedSeminarFitsEntry,
  seminarQueryKey,
  toSeminarApiFilters,
  writeSeminarListQuery,
} from './seminarListQuery';
import type { SeminarListQuery, SeminarSort, SeminarTypeTab } from './seminarListQuery';

import { designSystem } from '@/styles/theme';
import ConfirmModal from '@/components/common/elements/ConfirmModal';
import ResultModal from '@/components/common/elements/ResultModal';
import { ChevronLabel } from '@/components/common/elements/IconLabel';
import { useListKey, useUiScope } from '@/common/useUiScope';
import { UI_SCREEN } from '@/common/uiEventNames';

/**
 * 세미나 목록 (docs/seminar-filter-mobile-plan.md).
 *
 * 공통: 탭(전체·명치사 커리어·일반) 맨 위 → 조건 칩 한 줄(칩을 누르면 폰은 아래 시트, PC 는 칩 아래 팝업) → 목록.
 * 조건은 모두 주소 쿼리에 있다(seminarListQuery.ts) — 주소가 바뀌면 목록을 처음부터 다시 받는다.
 *
 * - PC(>768px): "전체" 탭 위쪽 명치사 커리어 섹션(4장+더보기, 1건이면 가로형) + 4열 그리드 + 페이지 번호 — 목록 구조는 예전 그대로.
 * - 폰(≤768px): 탭+칩 줄을 헤더 아래 고정, 무한 스크롤. 명치사 커리어는 폭 가득 1열 큰 카드, 일반 세미나는 2열 작은 카드.
 *   "전체" 탭은 커리어 섹션 없이 커리어 먼저 정렬한 목록 하나를 이어 받고, 유형이 바뀌는 곳에 소제목("명치사 커리어" / "일반 세미나")을 넣는다.
 */

interface SeminarBanner {
  id: number;
  type: 'SEMINAR_TOP' | 'SEMINAR_BOTTOM';
  title?: string;
  imageUrl: string;
  mobileImageUrl?: string;
  linkUrl?: string;
  displayOrder: number;
}

// api.category.list() 응답 형태 (등록 폼과 동일 소스)
interface MetaCategory { code: string; displayName: string }
interface MetaGroup { code: string; displayName: string; categories: MetaCategory[] }

const Container = ListPageShell;

// 페이지당 세미나 수 (PC 페이징 / 모바일 무한스크롤 공통)
const SEM_PAGE_SIZE = 32;
/** "전체" 탭 상단 커리어 섹션에 보이는 카드 수 — PC 1줄(4장) */
const CAREER_SECTION_CARDS = 4;
/** 커리어 섹션 개수 계산용으로 한 번에 받는 수 */
const CAREER_SECTION_FETCH_LIMIT = 50;
const SeminarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin-top: 16px;
  margin-bottom: 32px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 768px) {
    gap: 12px;
  }
`;

const TopRow = ListTabRow;
const TypeTabBar = ListTabs;
const TypeTabButton = ListTypeTab;

const ChipRow = ListFilterRow;

/** 검색어 줄(검색어가 있을 때만) */
const ListMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 24px;
  margin: 10px 0 2px;
  font-size: 14px;
  color: ${filterTokens.color.textSub};

  b {
    color: ${filterTokens.color.text};
    font-weight: 800;
  }

  @media (max-width: 768px) {
    margin: 14px 4px 8px;
  }
`;

const MetaText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ClearSearchButton = styled.button`
  flex-shrink: 0;
  margin-left: auto;
  padding: 4px 2px;
  border: none;
  background: none;
  font-family: inherit;
  font-size: 13px;
  color: ${filterTokens.color.textMuted};
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin: 4px 0 16px;
`;

const SectionTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-size: 17px;
  font-weight: 800;
  color: #111827;
  white-space: nowrap;
`;

const CareerDot = styled.i`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${CAREER_COLOR};
  flex-shrink: 0;
`;

const SectionCount = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #9ca3af;
`;

const SectionLink = styled.button`
  appearance: none;
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  font-size: 13px;
  color: #6b7280;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

/** 폰 "전체" 탭 목록 안 소제목 — 그리드 한 줄을 다 쓴다 */
const GroupHeading = styled.div<{ first: boolean }>`
  grid-column: 1 / -1;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin: ${({ first }) => (first ? '2px 4px -2px' : '16px 4px -2px')};
`;

/** 폰 목록에서 그리드 한 줄을 다 쓰는 카드 자리(명치사 커리어 큰 카드) */
const FullRow = styled.div`
  grid-column: 1 / -1;
  display: flex;
  min-width: 0;
`;

const CareerSectionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 768px) {
    gap: 12px;
  }
`;

/** 더보기 버튼이 없을 때 커리어 섹션과 "전체 세미나" 사이 간격 — 버튼 자리(56px)만큼 비우면 휑해서 24px(2026-09-29) */
const SectionGap = styled.div`
  height: 24px;
`;

/** "명치사 커리어 더보기 (N)" — 테두리·배경 없는 가운데 텍스트 링크(2026-09-28 사용자 피드백). 터치 영역 높이 36px */
const SectionMoreButton = styled.button`
  display: block;
  height: 36px;
  margin: 6px auto 14px;
  padding: 0 12px;
  border: none;
  border-radius: 6px;
  background: none;
  color: #6b7280;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    color: #111827;
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  &:focus-visible {
    outline: 2px solid ${CAREER_COLOR};
    outline-offset: 2px;
  }
`;


const Button = styled.button<{ variant?: 'primary' | 'secondary'; disabled?: boolean }>`
  padding: 12px 24px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background-color 0.2s;
  opacity: ${props => props.disabled ? 0.6 : 1};

  ${props => {
    if (props.disabled) {
      return `
        background-color: #f5f5f5;
        color: #999;
        &:hover {
          background-color: #f5f5f5;
        }
      `;
    }

    return props.variant === 'primary' ? `
      background-color: ${designSystem.colors.primary};
      color: white;
      &:hover {
        background-color: ${designSystem.colors.primaryDark};
      }
    ` : `
      background-color: #f5f5f5;
      color: #666;
      &:hover {
        background-color: #e8e8e8;
      }
    `;
  }}
`;

const FloatingButton = styled.button`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    bottom: calc(90px + env(safe-area-inset-bottom));
    right: 10px;
    width: 56px;
    height: 56px;
    background-color: ${designSystem.colors.primary};
    color: white;
    border: none;
    border-radius: 50%;
    cursor: pointer;
    font-size: 28px;
    font-weight: 300;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
    transition: all 0.3s ease;
    z-index: 1000;

    &:active {
      transform: scale(0.95);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
`;



/** "전체" 탭 커리어 섹션(PC) — 앞 4장 + 전체 개수. 개수는 탭을 보일지(0건이면 숨김)에도 쓴다 */
interface CareerSectionState {
  items: SeminarSummary[];
  count: number;
}

/** 뒤로가기로 돌아왔을 때 되살릴 목록 상태 (common/listRestore 참고) */
interface SeminarListSnapshot {
  filteredSeminars: SeminarSummary[];
  banners: SeminarBanner[];
  page: number;
  total: number;
  /** 모바일 무한스크롤 — 다음 페이지가 더 있는지 */
  hasMore: boolean;
  /** 이 목록을 받은 조건(seminarQueryKey) — 주소의 조건과 같을 때만 되살린다 */
  queryKey: string;
  /** 폰 목록(이어 받기)인지 — PC 페이지 목록과 섞지 않는다 */
  mobile: boolean;
  careerSection: CareerSectionState;
}

const SeminarList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // 조건은 주소가 원본 — 칩·시트·탭은 주소를 바꾸고, 주소가 바뀌면 목록을 다시 받는다
  const query: SeminarListQuery = useMemo(() => parseSeminarListQuery(searchParams), [searchParams]);
  const queryKey: string = seminarQueryKey(query);
  const queryRef = useRef<SeminarListQuery>(query);
  queryRef.current = query;
  const typeTab: SeminarTypeTab = query.tab;
  const user = useRecoilValue(userAtom);
  const isMobile: boolean = useIsMobileWidth();
  const headerHeight: number = useListHeaderHeight(true);

  // 세미나를 보고 뒤로 왔을 때: 다시 받지 않고 보던 목록·페이지·스크롤 위치를 그대로 되살린다.
  // 저장본의 조건·화면 종류(폰/PC)가 지금 주소와 같을 때만 쓴다 — 판정은 마운트 때 한 번
  const { restored, save } = useListRestore<SeminarListSnapshot>('seminars');
  const initialRestoreRef = useRef<SeminarListSnapshot | null | undefined>(undefined);
  if (initialRestoreRef.current === undefined) {
    initialRestoreRef.current = restored && restored.queryKey === queryKey && restored.mobile === isMobile ? restored : null;
  }
  const restoredSnapshot: SeminarListSnapshot | null = initialRestoreRef.current;
  useScrollRestore(restoredSnapshot !== null);
  /** 지금 화면의 조건+화면 종류 — 목록이 이 조건으로 받은 것일 때만 그린다 */
  const viewKey: string = `${queryKey}|${isMobile ? 'mobile' : 'pc'}`;
  const viewKeyRef = useRef<string>(viewKey);
  viewKeyRef.current = viewKey;
  // 노출·클릭 기록 — 조건·화면 종류가 바뀌면 새 목록 표시. "명치사 커리어" 묶음은 따로 센다
  const trackListKey: string = useListKey(`seminars:${viewKey}`);
  const listBodyRef = useUiScope({ screen: UI_SCREEN.SEMINAR_LIST, section: 'list', listKey: trackListKey });
  const careerRef = useUiScope({ screen: UI_SCREEN.SEMINAR_LIST, section: 'career', listKey: trackListKey });

  const [filteredSeminars, setFilteredSeminars] = useState<SeminarSummary[]>(restoredSnapshot?.filteredSeminars ?? []);
  const [banners, setBanners] = useState<SeminarBanner[]>(restoredSnapshot?.banners ?? []);
  // 페이징(PC) / 무한스크롤(모바일)
  const [page, setPage] = useState(restoredSnapshot?.page ?? 0);
  const [total, setTotal] = useState(restoredSnapshot?.total ?? 0);
  // 무한스크롤 종료 — 마지막 응답이 한 페이지보다 적거나 (page+1)*크기 >= total 이면 멈춘다.
  // (중복 제거 뒤 개수 < total 로 판정하면 경계가 밀린 목록에서 끝없이 요청한다)
  const [hasMore, setHasMore] = useState<boolean>(restoredSnapshot?.hasMore ?? false);
  const [loadingMore, setLoadingMore] = useState(false);
  /** 이어 받는 중 — 관찰자가 한 프레임 안에 두 번 불러도 요청은 한 번만 */
  const loadingMoreRef = useRef<boolean>(false);
  /**
   * 폰 무한스크롤의 다음 페이지를 못 받음 — 자동 이어 받기를 멈추고 목록 끝에 [다시 시도]를 보인다.
   * (예전엔 페이지 번호를 먼저 올리고 실패해도 hasMore 가 남아 offset 만 늘려 끝없이 요청했다)
   */
  const [loadMoreError, setLoadMoreError] = useState(false);
  /** 첫 페이지를 못 받음 — 빈 목록 안내("등록된 세미나가 없습니다") 대신 오류와 [다시 시도]를 보인다 */
  const [firstLoadError, setFirstLoadError] = useState(false);
  /** 첫 페이지를 받는 중 — 빈 목록 안내가 잠깐 비치지 않게 */
  const [loadingFirst, setLoadingFirst] = useState<boolean>(restoredSnapshot === null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  /** 마지막으로 보낸 목록 요청 번호 — 조건이 바뀐 뒤 늦게 온 옛 응답은 버린다 */
  const requestSeqRef = useRef<number>(0);
  /**
   * 지금 들고 있는 목록을 받은 조건(viewKey). 탭·조건을 바꾸면 새 응답이 올 때까지 이전 조건의 목록은 그리지 않는다
   * (예전엔 "전체" 목록이 잠깐 보였다가 커리어만으로 바뀌어 깜박였다). 되살린 목록은 지금 조건과 같으므로 바로 그린다
   */
  const [listKey, setListKey] = useState<string | null>(restoredSnapshot !== null ? viewKey : null);
  /** 폰에서 조건·탭을 바꾼 직후 — 새 목록을 받을 때 맨 위로 올린다 */
  const scrollTopPendingRef = useRef<boolean>(false);

  // 카테고리 필터 옵션 — 등록 폼과 동일하게 api.category.list()에서 로드해 그룹(임상/경영/기타)별로 유지.
  const [seminarCategoryGroups, setSeminarCategoryGroups] = useState<CategoryGroupOption[]>([]);

  const [careerSection, setCareerSection] = useState<CareerSectionState>(restoredSnapshot?.careerSection ?? { items: [], count: 0 });
  // 커리어 개수를 성공적으로 받았는지 — 0건이면 탭 자체를 숨기고 전체 목록만 보인다(사용자 결정 2026-09-28)
  const [careerChecked, setCareerChecked] = useState<boolean>(restoredSnapshot !== null);
  // 개수 조회 실패 — 탭을 숨기지 않는다(0건인지 모르므로)
  const [careerFailed, setCareerFailed] = useState(false);

  // 강사 지원 상태를 저장하는 state
  const [instructorApplicationStatus, setInstructorApplicationStatus] = useState<string | null>(null);

  // 사용자가 세미나 스피커 권한을 가지고 있는지 확인
  const canCreateSeminar = user?.role?.includes('ROLE_INSTRUCTOR') || false;

  // 조건(주소)·화면 종류가 바뀌면 처음부터 다시 받는다. 되살린 목록이면 첫 번은 건너뛴다
  const skipFirstLoadRef = useRef<boolean>(restoredSnapshot !== null);
  useEffect(() => {
    if (skipFirstLoadRef.current) {
      skipFirstLoadRef.current = false;
      return;
    }
    // 이전 조건의 목록은 비우고 첫 페이지 로딩 표시부터 — 새 응답으로만 채운다
    setFilteredSeminars([]);
    setTotal(0);
    setHasMore(false);
    setPage(0);
    setLoadMoreError(false);
    void loadSeminars(0, false);
    if (scrollTopPendingRef.current) {
      scrollTopPendingRef.current = false;
      // 시트가 닫히며 몸통 고정을 풀 때 제자리로 돌리므로 한 프레임 뒤에도 한 번 더 올린다
      window.scrollTo(0, 0);
      window.requestAnimationFrame(() => window.scrollTo(0, 0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey, isMobile]);

  // 커리어 개수(탭 표시)·PC 커리어 섹션 — 정렬이 바뀌면 섹션 순서도 따라간다
  const skipFirstCareerRef = useRef<boolean>(restoredSnapshot !== null);
  useEffect(() => {
    if (skipFirstCareerRef.current) {
      skipFirstCareerRef.current = false;
      return;
    }
    void loadCareerSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.sort]);

  // 로그인한 사용자만 강사 신청 상태 조회
  useEffect(() => {
    if (!user) {
      setInstructorApplicationStatus('NONE');
      return;
    }
    api.instructor.getMyApplications()
      .then((applicationsResponse: any) => {
        if (applicationsResponse.code === 0) {
          const applications = applicationsResponse.result;
          setInstructorApplicationStatus(applications && applications.length > 0 ? applications[0].status : 'NONE');
        } else {
          setInstructorApplicationStatus('NONE');
        }
      })
      .catch((error: unknown) => {
        console.error('Error fetching instructor status:', error);
        setInstructorApplicationStatus('NONE');
      });
  }, [user]);

  // 카테고리 옵션 로드 — 그룹 순서(임상 → 경영 → 기타)를 유지한 채 그룹별 카테고리 칩으로 사용
  useEffect(() => {
    api.category.list()
      .then((res: any) => {
        const groups: MetaGroup[] = res.result ?? [];
        const grouped: CategoryGroupOption[] = groups.map(g => ({
          groupName: g.displayName,
          categories: g.categories.map(c => ({ name: c.displayName, value: c.code })),
        }));
        setSeminarCategoryGroups(grouped);
      })
      .catch(console.error);
  }, []);

  // 모바일 무한스크롤: 센티넬이 보이면 다음 페이지 이어붙임
  useEffect(() => {
    if (!isMobile) return;
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver((entries) => {
      // 페이지 번호는 응답이 성공했을 때만 올린다(loadSeminars). 실패 뒤에는 [다시 시도]를 누를 때까지 멈춘다
      if (entries[0].isIntersecting && !loadingMoreRef.current && hasMore && !loadMoreError) {
        void loadSeminars(page + 1, true);
      }
    }, { rootMargin: '200px' });
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, loadingMore, hasMore, page, loadMoreError]);

  /** 목록 끝 [다시 시도] — 못 받은 그 페이지만 한 번 다시 요청한다 */
  const retryLoadMore = () => {
    if (loadingMoreRef.current) return;
    setLoadMoreError(false);
    void loadSeminars(page + 1, true);
  };

  const handleSeminarClick = (seminarId: string | number) => {
    navigate(`/seminars/${seminarId}`);
  };

  // 강사 신청 버튼 렌더링 함수(빈 목록 안내)
  const renderInstructorButton = () => {
    switch (instructorApplicationStatus) {
      case 'PENDING':
        return (
          <Button variant="secondary" disabled>
            강사 신청중
          </Button>
        );
      case 'APPROVED':
        return (
          <Button variant="primary" onClick={() => navigate('/seminars/new')}>
            세미나 등록
          </Button>
        );
      case 'REJECTED':
        return (
          <Button variant="secondary" onClick={() => navigate('/seminar/instructor/apply')}>
            강사 재신청하기
          </Button>
        );
      default: // 'NONE'
        return (
          <Button variant="secondary" onClick={() => navigate('/seminar/instructor/apply')}>
            강사 신청하기
          </Button>
        );
    }
  };

  /** PC 생성 버튼 — 탭 줄 오른쪽 끝(공용 ListCreateButton). 폰에서는 버튼이 스스로 숨는다 */
  const renderComposeAction = (): React.ReactNode => {
    if (canCreateSeminar || instructorApplicationStatus === 'APPROVED') {
      return <ListCreateButton type="button" onClick={() => navigate('/seminars/new')}>세미나 등록</ListCreateButton>;
    }
    if (instructorApplicationStatus === null) return null;
    if (instructorApplicationStatus === 'PENDING') {
      return <ListCreateButton type="button" disabled>강사 신청중</ListCreateButton>;
    }
    return (
      <ListCreateButton type="button" onClick={() => navigate('/seminar/instructor/apply')}>
        {instructorApplicationStatus === 'REJECTED' ? '강사 재신청하기' : '강사 신청하기'}
      </ListCreateButton>
    );
  };

  // 목록 상태가 바뀔 때마다 최신 상태를 기억해 둔다 (뒤로가기 복원용, 참조만 보관)
  useEffect(() => {
    // 다른 조건의 목록(또는 받는 중인 빈 목록)을 지금 조건으로 저장하지 않는다
    if (listKey !== viewKey) return;
    if (filteredSeminars.length === 0 && total === 0 && careerSection.items.length === 0) return;
    save({ filteredSeminars, banners, page, total, hasMore, queryKey, mobile: isMobile, careerSection });
  }, [filteredSeminars, banners, page, total, hasMore, queryKey, isMobile, careerSection, save, listKey, viewKey]);

  /** 한 페이지 결과를 화면에 반영 */
  const applyPage = (list: SeminarSummary[], totalCount: number, append: boolean, pageNum: number, pageBanners?: SeminarBanner[]) => {
    setHasMore(list.length >= SEM_PAGE_SIZE && (pageNum + 1) * SEM_PAGE_SIZE < totalCount);
    // 이어붙일 때는 이미 있는 id 를 뺀다(모바일 무한스크롤에서 페이지 사이 경계가 밀려도 같은 카드가 두 번 안 보이게)
    setFilteredSeminars(prev => {
      if (!append) return list;
      const seen: Set<string> = new Set(prev.map(item => String(item.id)));
      return [...prev, ...list.filter(item => !seen.has(String(item.id)))];
    });
    setTotal(totalCount);
    if (!append && pageBanners) setBanners(pageBanners);
  };

  // 현재 조건(주소)으로 특정 페이지 조회. append=true면 이어붙임(모바일 무한스크롤)
  // - 유형 거르기는 서버만 한다: 커리어/일반 탭은 seminarType 을 넘기고 받은 목록을 그대로 쓴다.
  // - 전체 탭: 서버에 sort=careerFirst(,startSoon) 를 넘기고, 받은 페이지 안에서도 커리어를 앞으로 안정 정렬한다(거르기 아님).
  const loadSeminars = async (pageNum: number, append: boolean) => {
    const currentQuery: SeminarListQuery = queryRef.current;
    const requestKey: string = viewKeyRef.current;
    const seq: number = append ? requestSeqRef.current : ++requestSeqRef.current;
    const offset = pageNum * SEM_PAGE_SIZE;
    if (append) {
      if (loadingMoreRef.current) return;
      loadingMoreRef.current = true;
      setLoadingMore(true);
    } else {
      setLoadingFirst(true);
      setFirstLoadError(false);
    }
    try {
      const filters: Record<string, string> = toSeminarApiFilters(currentQuery);
      const response = await api.seminar.getList(SEM_PAGE_SIZE, offset, filters);
      if (seq !== requestSeqRef.current) return; // 응답 전에 조건이 바뀜
      if (response.code === 0) {
        const list: SeminarSummary[] = response.result.list || [];
        const pageBanners: SeminarBanner[] = response.result.banners || [];
        applyPage(currentQuery.tab === 'all' ? sortCareerFirst(list) : list, response.result.total || 0, append, pageNum, pageBanners);
        if (append) {
          setPage(pageNum);
          setLoadMoreError(false);
        }
        setListKey(requestKey);
      } else if (append) {
        setLoadMoreError(true);
      } else {
        setFilteredSeminars([]);
        setTotal(0);
        setHasMore(false);
        setFirstLoadError(true);
        setListKey(requestKey);
      }
    } catch (error) {
      console.error('세미나 조회 오류:', error);
      if (seq === requestSeqRef.current) {
        if (append) setLoadMoreError(true);
        else { setFilteredSeminars([]); setTotal(0); setHasMore(false); setFirstLoadError(true); setListKey(requestKey); }
      }
    } finally {
      if (append) {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
      else if (seq === requestSeqRef.current) setLoadingFirst(false);
    }
  };

  // 커리어 개수(탭 표시 여부) + PC "전체" 탭 상단 커리어 섹션 — 서버가 seminarType 으로 거른 total·list 를 그대로 쓴다
  const loadCareerSection = async () => {
    try {
      const filters: Record<string, string> = { seminarType: 'CAREER' };
      if (queryRef.current.sort !== 'latest') filters.sort = queryRef.current.sort;
      const response = await api.seminar.getList(CAREER_SECTION_FETCH_LIMIT, 0, filters);
      if (response.code !== 0) {
        setCareerFailed(true);
        return;
      }
      const list: SeminarSummary[] = response.result.list || [];
      setCareerSection({
        items: list.slice(0, CAREER_SECTION_CARDS),
        count: response.result.total || list.length,
      });
      setCareerFailed(false);
      setCareerChecked(true);
    } catch (error) {
      console.error('커리어 세미나 조회 오류:', error);
      setCareerFailed(true);
    }
  };

  /** 조건을 주소에 쓴다(되돌리기 칸을 늘리지 않게 replace) — 주소가 바뀌면 위 효과가 목록을 다시 받는다 */
  const applyQuery = (next: SeminarListQuery) => {
    if (seminarQueryKey(next) === queryKey) return;
    if (isMobile) scrollTopPendingRef.current = true;
    setSearchParams(prev => {
      const params: URLSearchParams = writeSeminarListQuery(prev, next);
      return params.toString() === prev.toString() ? prev : params;
    }, { replace: true, state: FILTER_TOUCHED_STATE });
  };

  // 조건 없이 들어오면 저장한 조건(탭 포함)을 켠다 — 정렬은 지금 것 그대로
  const savedEntry = useSavedFilterOnEntry('seminar', isPlainSeminarEntry(searchParams) && restoredSnapshot === null, (savedQuery: string) => {
    const saved: SeminarListQuery = parseSavedSeminarQuery(savedQuery);
    if (savedSeminarFitsEntry(queryRef.current, saved)) applyQuery({ ...saved, sort: queryRef.current.sort });
  });

  const handleTypeTabChange = (tab: SeminarTypeTab) => {
    if (tab === typeTab) return;
    applyQuery({ ...query, tab, formats: normalizeFormatsForTab(query.formats, tab) });
  };

  /** 커리어 섹션의 "수료하면…" / "더보기" — 커리어 탭으로 이동하고 탭 위치로 스크롤 */
  const topAreaRef = useRef<HTMLDivElement>(null);
  const goCareerTab = () => {
    handleTypeTabChange('career');
    if (isMobile) return; // 폰은 목록을 새로 받으며 맨 위로 올라간다
    const top = topAreaRef.current ? topAreaRef.current.getBoundingClientRect().top + window.scrollY - 80 : 0;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  };

  const handleRegisterClick = () => navigate('/seminars/new');

  // FAB(+ 버튼) 클릭 — 권한 별 분기
  const [showInstructorConfirm, setShowInstructorConfirm] = useState(false);
  const [showPendingAlert, setShowPendingAlert] = useState(false);

  const handleFabClick = () => {
    if (canCreateSeminar) {
      handleRegisterClick();
      return;
    }
    // 강사 권한 없음 — 신청 상태에 따라 분기
    if (instructorApplicationStatus === 'PENDING') {
      setShowPendingAlert(true);
      return;
    }
    setShowInstructorConfirm(true);
  };

  const handleBannerClick = (banner: SeminarBanner) => {
    if (banner.linkUrl) {
      window.open(banner.linkUrl, '_blank');
    }
  };

  // 상단·하단 배너는 공용 자리(PageBannerSlot) — 여기서는 상단 유무만 본다(같은 요청 공유)
  const pageBanners = usePageBanners();
  const conditionApplied: boolean = hasSeminarCondition(query);
  // PC "전체" 탭 상단 커리어 섹션 — 조건이 없을 때만
  const showCareerSection: boolean = !isMobile && typeTab === 'all' && !conditionApplied && careerSection.items.length > 0;
  // 폰 "전체" 탭 — 커리어가 있으면 목록 안에 유형 소제목
  const showGroupHeadings: boolean = isMobile && typeTab === 'all' && careerSection.count > 0;
  const showTabs: boolean = careerSection.count > 0 || careerFailed;
  // 커리어가 0건으로 확인되면 탭이 없으므로 URL 로 들어온 career/general 탭도 전체로 되돌린다
  useEffect(() => {
    if (careerChecked && careerSection.count === 0 && typeTab !== 'all') handleTypeTabChange('all');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [careerChecked, careerSection.count, typeTab]);
  // 커리어 섹션에 이미 보인 카드는 아래 "전체 세미나"에서 뺀다(중복 방지). 받은 목록·페이지 계산은 그대로 두고 그릴 때만 거른다
  // 목록이 지금 조건으로 받은 것이 아니면(조건을 막 바꿈) 비어 있는 것으로 보고 로딩 표시만 한다
  // 저장한 조건을 읽는 중이면 아직 그리지 않는다 — 기본 목록이 잠깐 보였다가 바뀌지 않게
  const listReady: boolean = listKey === viewKey && !savedEntry.waiting;
  const shownSeminars: SeminarSummary[] = listReady ? filteredSeminars : [];
  const showFirstLoading: boolean = loadingFirst || !listReady;
  const sectionIds: Set<string> = new Set(showCareerSection ? careerSection.items.map(item => String(item.id)) : []);
  const visibleSeminars: SeminarSummary[] = shownSeminars.filter(item => !sectionIds.has(String(item.id)));
  const restCount: number = Math.max(0, total - sectionIds.size);

  // 탭 방향키 이동(←/→, Home/End) — WAI-ARIA 탭 패턴. 이동과 동시에 선택한다
  const tabButtonRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const handleTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last: number = SEMINAR_TYPE_TABS.length - 1;
    const next: number | null =
      event.key === 'ArrowRight' ? (index === last ? 0 : index + 1)
        : event.key === 'ArrowLeft' ? (index === 0 ? last : index - 1)
          : event.key === 'Home' ? 0
            : event.key === 'End' ? last
              : null;
    if (next === null) return;
    event.preventDefault();
    tabButtonRefs.current[next]?.focus();
    handleTypeTabChange(SEMINAR_TYPE_TABS[next].key);
  };
  const emptyMessage: string = conditionApplied
    ? '조건에 맞는 세미나가 없어요.'
    : typeTab === 'career'
      ? '아직 명치사 커리어 세미나가 없어요.'
      : typeTab === 'general'
        ? '등록된 일반 세미나가 없어요.'
        : '등록된 세미나가 없습니다.';

  /** 폰 목록: 커리어는 1열 큰 카드, 일반은 2열. "전체" 탭은 유형이 바뀌는 곳에 소제목 */
  const renderGridItems = (): React.ReactNode[] => {
    const items: React.ReactNode[] = [];
    let previousCareer: boolean | null = null;
    visibleSeminars.forEach((seminar: SeminarSummary) => {
      const career: boolean = isCareerSeminar(seminar);
      // 맨 위 커리어 소제목 자리에는 조작 줄(무료만 보기·정렬)이 들어간다 — 소제목은 일반 세미나로 넘어가는 곳에만(2026-10-01)
      if (showGroupHeadings && !career && career !== previousCareer) {
        items.push(
          <GroupHeading key={`heading-${seminar.id}`} first={previousCareer === null} role="presentation">
            <SectionTitle>일반 세미나</SectionTitle>
          </GroupHeading>,
        );
      }
      previousCareer = career;
      // 폰: 커리어는 PC 세로형과 같은 정보 구성으로 1열, 본문은 폰 1열에 맞게 확대. 일반은 기존 2열 카드.
      items.push(career ? (
        <FullRow key={seminar.id}>
          <SeminarCard seminar={seminar} onClick={handleSeminarClick} fullWidthMobile />
        </FullRow>
      ) : (
        <SeminarCard key={seminar.id} seminar={seminar} onClick={handleSeminarClick} />
      ));
    });
    return items;
  };

  // 탭이 없어도(커리어 없음) PC 에서 생성 버튼이 있으면 탭 줄을 그려 버튼 자리를 만든다
  const createButton: React.ReactNode = renderComposeAction();
  const showTopRow: boolean = showTabs || (!isMobile && createButton !== null);

  return (
    <Container data-list-page="seminar" ref={topAreaRef}>
        {showTopRow && <TopRow style={{ top: headerHeight }}>
            {showTabs && <TypeTabBar role="tablist" aria-label="세미나 구분">
              {SEMINAR_TYPE_TABS.map((tab, index) => (
                <TypeTabButton
                  key={tab.key}
                  ref={(element: HTMLButtonElement | null) => { tabButtonRefs.current[index] = element; }}
                  id={`seminar-type-tab-${tab.key}`}
                  type="button"
                  role="tab"
                  aria-selected={typeTab === tab.key}
                  aria-controls="seminar-type-panel"
                  tabIndex={typeTab === tab.key ? 0 : -1}
                  active={typeTab === tab.key}
                  onClick={() => handleTypeTabChange(tab.key)}
                  onKeyDown={(event: React.KeyboardEvent<HTMLButtonElement>) => handleTabKeyDown(event, index)}
                >
                  {tab.label}
                </TypeTabButton>
              ))}
            </TypeTabBar>}
            {createButton}
        </TopRow>}
        <ChipRow>
          <SeminarFilterBar
            query={query}
            categoryGroups={seminarCategoryGroups}
            isMobile={isMobile}
            onApply={applyQuery}
          />
        </ChipRow>

      <ListBody ref={listBodyRef}>
      {isMobile && (
        // 아래 내용의 위 여백: 배너 12px, 검색 안내 10px, 카드 목록 16px
        <ListControlRow style={listControlGap(pageBanners.top.length > 0 ? 12 : query.search && shownSeminars.length > 0 ? 10 : 16)}>
          <CheckToggle label="무료만 보기" checked={query.freeOnly} onChange={(freeOnly: boolean) => applyQuery({ ...query, freeOnly })} />
          <SortSelect options={SEMINAR_SORT_OPTIONS} value={query.sort} defaultValue="latest" onChange={(sort: SeminarSort) => applyQuery({ ...query, sort })} />
        </ListControlRow>
      )}
      <PageBannerSlot placement="top" variant="list" />

      <div role={showTabs ? 'tabpanel' : undefined} id="seminar-type-panel" aria-labelledby={showTabs ? `seminar-type-tab-${typeTab}` : undefined}>
      {showCareerSection && (
        <section ref={careerRef} aria-label="명치사 커리어" style={{ marginTop: 16 }}>
          <SectionHeader>
            <SectionTitle><CareerDot aria-hidden="true" />명치사 커리어</SectionTitle>
            <SectionLink type="button" onClick={goCareerTab}><ChevronLabel truncate>수료하면 내 프로필·이력서에 남아요</ChevronLabel></SectionLink>
          </SectionHeader>
          {/* 1건이면 2열 중 한 칸만 차고 반이 비지 않게 전체 폭 가로형(태블릿·PC 는 목록 폭 전체), 2건 이상은 그리드 */}
          {careerSection.items.length === 1 ? (
            <SeminarCard seminar={careerSection.items[0]} onClick={handleSeminarClick} layout="horizontal" />
          ) : (
            <CareerSectionGrid>
              {careerSection.items.map(seminar => (
                <SeminarCard key={seminar.id} seminar={seminar} onClick={handleSeminarClick} />
              ))}
            </CareerSectionGrid>
          )}
          {/* 더 볼 커리어가 없으면(전체 수 ≤ 보이는 카드 수) 더보기 버튼을 두지 않는다 */}
          {careerSection.count > careerSection.items.length ? (
            <SectionMoreButton type="button" onClick={goCareerTab}>
              명치사 커리어 더보기 ({careerSection.count})
            </SectionMoreButton>
          ) : (
            // 버튼이 없으면 아래 "전체 세미나"와 24px 만 띄운다
            <SectionGap aria-hidden="true" />
          )}
          {visibleSeminars.length > 0 && (
            <SectionHeader>
              <SectionTitle>전체 세미나 <SectionCount>{restCount}</SectionCount></SectionTitle>
            </SectionHeader>
          )}
        </section>
      )}

      {/* "N개 세미나" 줄은 없앤다(2026-09-29 사용자) — 검색어가 있을 때만 검색어와 [검색어 지우기] */}
      {!showCareerSection && shownSeminars.length > 0 && query.search && (
        <ListMeta aria-live="polite">
          <MetaText>‘{query.search}’ 검색 결과</MetaText>
          <ClearSearchButton type="button" onClick={() => applyQuery({ ...query, search: '' })}>검색어 지우기</ClearSearchButton>
        </ListMeta>
      )}

      {shownSeminars.length === 0 ? (
        showFirstLoading ? (
          <LoadingSpinner placement="page" minHeight="40vh" />
        ) : firstLoadError ? (
          <PageState
            title="세미나를 불러오지 못했어요"
            description="네트워크 상태를 확인한 뒤 다시 시도해 주세요."
            error
            onRetry={() => { void loadSeminars(page, false); }}
          />
        ) : (
          <EmptyState>
            <Text size="1.1rem" textcolor="#666">
              {emptyMessage}
            </Text>
            {conditionApplied ? (
              <div style={{ marginTop: '20px' }}>
                <Button variant="secondary" onClick={() => applyQuery(clearSeminarConditions(query))}>
                  조건 모두 풀기
                </Button>
              </div>
            ) : canCreateSeminar ? (
              <Button
                variant="primary"
                onClick={handleRegisterClick}
                style={{ marginTop: '20px' }}
              >
                첫 세미나 등록하기
              </Button>
            ) : (
              <div style={{ marginTop: '20px' }}>
                {renderInstructorButton()}
              </div>
            )}
          </EmptyState>
        )
      ) : (
        <>
          <SeminarGrid>
            {isMobile
              ? renderGridItems()
              : visibleSeminars.map(seminar => (
                <SeminarCard
                  key={seminar.id}
                  seminar={seminar}
                  onClick={handleSeminarClick}
                />
              ))}
          </SeminarGrid>

          {/* PC: 페이징(공지사항과 동일) / 모바일: 무한스크롤 */}
          {!isMobile && total > SEM_PAGE_SIZE && (
            <Pagination
              currentPage={page}
              totalPages={Math.ceil(total / SEM_PAGE_SIZE)}
              onPageChange={(p) => { setPage(p); loadSeminars(p, false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            />
          )}
          {isMobile && hasMore && !loadMoreError && (
            <div ref={loadMoreRef} style={{ height: 1 }} />
          )}
          {loadingMore && <LoadingSpinner />}
          {isMobile && loadMoreError && !loadingMore && (
            <PageState
              title="불러오지 못했어요"
              description="네트워크 상태를 확인한 뒤 다시 시도해 주세요."
              error
              onRetry={retryLoadMore}
            />
          )}

          <PageBannerSlot placement="bottom" variant="list" />
        </>
      )}

      </div>

      </ListBody>

      <FloatingButton onClick={handleFabClick} aria-label="세미나 등록">
          <AddRoundedIcon fontSize="inherit" />
      </FloatingButton>

      <ConfirmModal
        open={showInstructorConfirm}
        onClose={() => setShowInstructorConfirm(false)}
        onConfirm={() => navigate('/seminar/instructor/apply')}
        title="강사 신청 하시겠습니까?"
        description="세미나 등록은 강사 권한이 있어야 가능합니다. 신청 페이지로 이동합니다."
        confirmText="강사 신청하기"
        cancelText="취소"
      />

      <ResultModal
        open={showPendingAlert}
        onClose={() => setShowPendingAlert(false)}
        status={true}
        title="강사 신청 검토 중"
        description="이미 신청하신 강사 권한이 검토 중입니다. 승인 후 세미나 등록이 가능합니다."
      />

    </Container>
  );
};

export default SeminarList;
